import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthCohortDefinition, GrowthIntelligenceService, parseJsonRows } from './growth-intelligence.service';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc } from './growth-datetime.util';

interface CohortRuleRow {
  ruleType: string;
  value: string;
}

interface RetentionRow {
  week?: number;
  retained?: number;
  retentionRate?: number;
}

@Component({
  standalone: true,
  selector: 'ain-growth-cohorts',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, JalaliDateInputComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, DialogModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.intelligence.cohorts.title')" [description]="t('growth.intelligence.cohorts.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.intelligence.cohorts.help.title'" [descriptionKey]="'growth.intelligence.cohorts.help.description'"
        [bulletKeys]="['growth.intelligence.cohorts.help.bullets.rules', 'growth.intelligence.cohorts.help.bullets.advisory']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.intelligence.cohorts.actions.newCohort')" (click)="openCohortDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (cohorts().length === 0) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          <p-table [value]="cohorts()" [rows]="8" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.intelligence.cohorts.columns.name') }}</th>
                <th>{{ t('growth.intelligence.cohorts.columns.key') }}</th>
                <th>{{ t('growth.intelligence.cohorts.columns.status') }}</th>
                <th>{{ t('growth.intelligence.cohorts.columns.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.name }}</td>
                <td>{{ row.cohortKey }}</td>
                <td>{{ row.status }}</td>
                <td><button pButton type="button" size="small" [label]="t('common.select')" (click)="selectCohort(row)"></button></td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <section class="card">
        <div class="section-title"><h3>{{ t('growth.intelligence.cohorts.sections.refresh') }}</h3></div>
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.cohorts.fields.periodStart')"><ain-date-input [(ngModel)]="refreshForm.periodStartUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.cohorts.fields.periodEnd')"><ain-date-input [(ngModel)]="refreshForm.periodEndUtc" /></ain-form-field>
        </div>
        <button pButton type="button" [disabled]="!selectedCohortId" [label]="t('growth.intelligence.cohorts.actions.refreshSnapshot')" (click)="refreshSnapshot()"></button>
      </section>

      <section class="card">
        <div class="section-title"><h3>{{ t('growth.intelligence.cohorts.sections.retention') }}</h3></div>
        @if (retentionRows().length === 0) {
          <ain-state type="empty" [title]="t('growth.intelligence.cohorts.emptyRetention')" />
        } @else {
          <p-table [value]="retentionRows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.intelligence.cohorts.columns.week') }}</th>
                <th>{{ t('growth.intelligence.cohorts.columns.retained') }}</th>
                <th>{{ t('growth.intelligence.cohorts.columns.retentionRate') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.week ?? '-' }}</td>
                <td>{{ row.retained ?? 0 }}</td>
                <td>{{ row.retentionRate ?? 0 }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      <p-dialog
        [header]="t('growth.intelligence.cohorts.actions.newCohort')"
        [visible]="cohortDialogVisible()"
        (visibleChange)="cohortDialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(760px, 96vw)' }">
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.cohorts.fields.name')"><input pInputText [(ngModel)]="cohortForm.name" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.cohorts.fields.cohortKey')"><input pInputText [(ngModel)]="cohortForm.cohortKey" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.cohorts.fields.status')">
            <p-select [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="cohortForm.status" appendTo="body" />
          </ain-form-field>
        </div>
        <div class="section-title"><h4>{{ t('growth.intelligence.cohorts.sections.rules') }}</h4></div>
        @for (row of ruleRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.intelligence.cohorts.fields.ruleType')">
              <p-select [options]="ruleTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.ruleType" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.intelligence.cohorts.fields.ruleValue')"><input pInputText [(ngModel)]="row.value" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeRule($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addRule()"></button>
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="cohortDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveCohort()"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; margin-bottom: 8px; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthCohortsComponent implements OnInit {
  private readonly service = inject(GrowthIntelligenceService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly cohortDialogVisible = signal(false);
  readonly cohorts = signal<GrowthCohortDefinition[]>([]);
  readonly retentionRows = signal<RetentionRow[]>([]);

  selectedCohortId: string | null = null;
  cohortForm = { name: '', cohortKey: '', status: 'Active' };
  ruleRows: CohortRuleRow[] = [{ ruleType: 'segment', value: 'default' }];
  refreshForm = { periodStartUtc: defaultCalendarFromUtc(), periodEndUtc: defaultCalendarToUtc() };

  readonly statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Archived', label: 'Archived' }
  ];
  readonly ruleTypeOptions = [
    { value: 'segment', label: 'Segment' },
    { value: 'industry', label: 'Industry' },
    { value: 'channel', label: 'Channel' }
  ];

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    this.service.listCohorts().subscribe({
      next: items => { this.cohorts.set(items); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  openCohortDialog(): void {
    this.cohortForm = { name: '', cohortKey: '', status: 'Active' };
    this.ruleRows = [{ ruleType: 'segment', value: 'default' }];
    this.cohortDialogVisible.set(true);
  }

  saveCohort(): void {
    this.service.upsertCohort({
      cohortKey: this.cohortForm.cohortKey || null,
      name: this.cohortForm.name,
      cohortRuleJson: JSON.stringify(this.ruleRows),
      status: this.cohortForm.status
    }).subscribe({
      next: item => {
        this.selectedCohortId = item.id;
        this.cohortDialogVisible.set(false);
        this.load();
      }
    });
  }

  selectCohort(row: GrowthCohortDefinition): void {
    this.selectedCohortId = row.id;
    this.cohortForm = { name: row.name, cohortKey: row.cohortKey, status: row.status };
    this.ruleRows = parseJsonRows<CohortRuleRow>(row.cohortRuleJson, this.ruleRows);
    this.service.listCohortSnapshots(row.id).subscribe({
      next: snapshots => {
        const latest = snapshots[0];
        this.retentionRows.set(latest ? parseJsonRows<RetentionRow>(latest.retentionCurveJson) : []);
      }
    });
  }

  refreshSnapshot(): void {
    if (!this.selectedCohortId) return;
    this.service.refreshCohort(this.selectedCohortId, {
      periodStartUtc: combineUtcDateAndTime(this.refreshForm.periodStartUtc, '00:00')!,
      periodEndUtc: combineUtcDateAndTime(this.refreshForm.periodEndUtc, '23:59')!
    }).subscribe({
      next: snapshot => this.retentionRows.set(parseJsonRows<RetentionRow>(snapshot.retentionCurveJson))
    });
  }

  addRule(): void { this.ruleRows.push({ ruleType: 'segment', value: '' }); }
  removeRule(index: number): void { this.ruleRows.splice(index, 1); }
  t(key: string): string { return this.i18n.translate(key); }
}
